export interface BrowserIconSystem {
  activeProvider?: string;
  defaultIcons?: Record<string, string>;
  fontAwesomeMappings?: Record<string, string>;
}

declare global {
  interface Window {
    digitalAgoraIconSystem?: BrowserIconSystem;
  }
}

const materialSymbolsTokenPrefix = 'ms:';
const materialSymbolsFilledTokenPrefix = 'ms-filled:';
const fontAwesomeIconClassPattern =
  /\b(?:fa-solid|fa-regular|fa-brands|fa-[a-z0-9-]+|fas|far|fab|fa)\b/gi;
const nonIconFontAwesomeClasses = new Set([
  'fa-solid',
  'fa-regular',
  'fa-brands',
  'fa-spin',
  'fa-beat',
  'fa-2x',
]);

export function resolveIconToken(icon?: string | null): string {
  const value = icon?.trim();

  if (!value) {
    return '';
  }

  if (value.startsWith('app:')) {
    return window.digitalAgoraIconSystem?.defaultIcons?.[value.slice(4)] ?? '';
  }

  if (
    value.startsWith(materialSymbolsTokenPrefix) ||
    value.startsWith(materialSymbolsFilledTokenPrefix)
  ) {
    return value;
  }

  const normalizedFontAwesomeToken = normalizeFontAwesomeToken(value);
  if (!normalizedFontAwesomeToken) {
    return value;
  }

  return window.digitalAgoraIconSystem?.fontAwesomeMappings?.[normalizedFontAwesomeToken] ?? value;
}

export function initLegacyFontAwesomeIconUpgrade() {
  const upgrade = (root: ParentNode) => upgradeLegacyFontAwesomeIcons(root);

  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', () => upgrade(document), { once: true });
  } else {
    upgrade(document);
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            upgrade(node);
          }
        });
      }

      if (mutation.type === 'attributes' && mutation.target instanceof Element) {
        upgrade(mutation.target);
      }
    }
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true,
  });
}

export function getMaterialSymbolMask(name: string, variation: 'default' | 'fill1') {
  const encodedName = encodeURIComponent(name);
  const url = `https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/${encodedName}/${variation}/24px.svg`;

  return `url("${url}") center / contain no-repeat`;
}

function upgradeLegacyFontAwesomeIcons(root: ParentNode) {
  const elements =
    root instanceof Element && hasFontAwesomeClass(root)
      ? [root, ...Array.from(root.querySelectorAll('[class*="fa-"]'))]
      : Array.from(root.querySelectorAll('[class*="fa-"]'));

  elements.forEach((element) => {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    upgradeElement(element);
  });
}

function upgradeElement(element: HTMLElement) {
  const sourceClassName = element.className;
  const normalizedFontAwesomeToken = normalizeFontAwesomeToken(sourceClassName);

  if (!normalizedFontAwesomeToken) {
    return;
  }

  const resolvedToken = resolveIconToken(normalizedFontAwesomeToken);
  if (!resolvedToken || resolvedToken === normalizedFontAwesomeToken) {
    return;
  }

  const remainingClasses = sourceClassName
    .split(/\s+/)
    .map((className) => (className === 'fa-spin' ? 'app-icon-spin' : className))
    .filter((className) => className && !isFontAwesomeIconClass(className));

  if (resolvedToken.startsWith(materialSymbolsFilledTokenPrefix)) {
    applyMaterialSymbolIcon(
      element,
      resolvedToken.slice(materialSymbolsFilledTokenPrefix.length),
      'fill1',
      remainingClasses,
    );
    return;
  }

  if (resolvedToken.startsWith(materialSymbolsTokenPrefix)) {
    applyMaterialSymbolIcon(
      element,
      resolvedToken.slice(materialSymbolsTokenPrefix.length),
      'default',
      remainingClasses,
    );
    return;
  }

  element.className = [...remainingClasses, resolvedToken].join(' ');
}

function applyMaterialSymbolIcon(
  element: HTMLElement,
  name: string,
  variation: 'default' | 'fill1',
  remainingClasses: string[],
) {
  const style = element.style as CSSStyleDeclaration & { webkitMask?: string };
  const mask = getMaterialSymbolMask(name, variation);

  element.className = [...remainingClasses, 'app-icon-material'].join(' ');
  style.setProperty('--app-icon-mask', mask);
  style.webkitMask = 'var(--app-icon-mask)';
  style.mask = 'var(--app-icon-mask)';
  style.backgroundColor = 'currentColor';
  element.setAttribute('aria-hidden', element.getAttribute('aria-label') ? 'false' : 'true');
}

function hasFontAwesomeClass(element: Element) {
  return (
    typeof element.className === 'string' &&
    /\b(?:fa|fas|far|fab|fa-solid|fa-regular|fa-brands|fa-[a-z0-9-]+)\b/i.test(element.className)
  );
}

function normalizeFontAwesomeToken(value: string): string | null {
  const classes =
    value.match(fontAwesomeIconClassPattern)?.map((className) => className.toLowerCase()) ?? [];
  if (!classes.length) {
    return null;
  }

  const style =
    classes.includes('fa-brands') || classes.includes('fab')
      ? 'fa-brands'
      : classes.includes('fa-regular') || classes.includes('far')
        ? 'fa-regular'
        : 'fa-solid';
  const icon = classes.find(
    (className) => className.startsWith('fa-') && !nonIconFontAwesomeClasses.has(className),
  );

  return icon ? `${style} ${icon}` : null;
}

function isFontAwesomeIconClass(className: string) {
  const normalizedClassName = className.toLowerCase();

  return (
    normalizedClassName === 'fa' ||
    normalizedClassName === 'fas' ||
    normalizedClassName === 'far' ||
    normalizedClassName === 'fab' ||
    normalizedClassName.startsWith('fa-')
  );
}
